import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, PlayCircle, CheckCircle, BookOpen, ChevronRight, Send, Loader, FileText, Zap, MessageCircle, ChevronDown, Bookmark, XCircle } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { translations } from '../../translations';
import { getMyProgress, updateVideoProgress, startPractice, submitPracticeAnswer, getConceptMessages, sendConceptMessage, getMyFlags, toggleQuestionFlag } from '../../services/studioApi';
import { pixelChat, getPixelSessions, getPixelSession } from '../../services/api';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import UnifiedHeader from '../../components/UnifiedHeader';
import VideoReaction from '../../components/VideoReaction';
import TranscriptViewModal from '../../components/TranscriptViewModal';
import './Studio.css';
import './ConceptChat.css';

// ── Practice tab state machine ──────────────────────────────────────────────
// idle → loading → question → evaluating → feedback → (next question | done)
// ────────────────────────────────────────────────────────────────────────────

function PracticeTab({ conceptId, tStudent }) {
  const [practiceState, setPracticeState] = useState('idle'); // idle|loading|question|evaluating|done
  const [session, setSession] = useState(null); // { session_id, question, turn_number, total_questions }
  const [answer, setAnswer] = useState('');
  const [feedback, setFeedback] = useState(null); // { is_correct, feedback, next_question, ... }
  const [summary, setSummary] = useState(null);  // { score_pct, correct, asked, new_mastery }
  const [error, setError] = useState(null);
  const answerRef = useRef(null);

  const handleStart = async () => {
    setPracticeState('loading');
    setError(null);
    try {
      const data = await startPractice(conceptId);
      setSession(data);
      setPracticeState('question');
      setTimeout(() => answerRef.current?.focus(), 100);
    } catch (e) {
      setError(e.message);
      setPracticeState('idle');
    }
  };

  const handleSubmit = async () => {
    if (!answer.trim() || practiceState === 'evaluating') return;
    setPracticeState('evaluating');
    setError(null);
    try {
      const result = await submitPracticeAnswer(session.session_id, { answer: answer.trim() });
      setFeedback(result);
      if (result.done) {
        setSummary(result);
        setPracticeState('done');
      } else {
        setPracticeState('feedback');
      }
    } catch (e) {
      setError(e.message);
      setPracticeState('question');
    }
  };

  const handleNext = () => {
    setSession(prev => ({
      ...prev,
      question: feedback.next_question,
      question_type: feedback.next_question_type,
      turn_number: feedback.turn_number,
    }));
    setAnswer('');
    setFeedback(null);
    setPracticeState('question');
    setTimeout(() => answerRef.current?.focus(), 100);
  };

  const handleStop = async () => {
    if (!session) return;
    try {
      const result = await submitPracticeAnswer(session.session_id, { answer: '', stop: true });
      setSummary(result);
      setPracticeState('done');
    } catch {
      setPracticeState('done');
    }
  };

  const handleRetry = () => {
    setSession(null);
    setAnswer('');
    setFeedback(null);
    setSummary(null);
    setError(null);
    setPracticeState('idle');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmit(); }
  };

  // ── IDLE ──
  if (practiceState === 'idle') return (
    <div style={{ textAlign: 'center', padding: '2.5rem 1.5rem' }}>
      <div style={{ width: 56, height: 56, borderRadius: '50%', background: 'linear-gradient(135deg,#667eea,#764ba2)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
        <Zap size={26} color="white" />
      </div>
      <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.05rem' }}>{tStudent.practiceStart || 'Start Practice'}</h3>
      <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: '1.5rem', maxWidth: 360, margin: '0 auto 1.5rem' }}>
        {tStudent.practiceStartDesc || 'AI asks you 5 questions one at a time and explains any mistakes.'}
      </p>
      {error && <p style={{ color: '#e53935', fontSize: '0.85rem', marginBottom: '0.75rem' }}>{error}</p>}
      <button className="btn-primary" style={{ padding: '0.75rem 2rem', fontSize: '0.95rem' }} onClick={handleStart}>
        <Zap size={15} /> {tStudent.practiceStart || 'Start Practice'}
      </button>
    </div>
  );

  // ── LOADING ──
  if (practiceState === 'loading') return (
    <div style={{ textAlign: 'center', padding: '3rem 1rem' }}>
      <div className="spinner" />
      <p style={{ color: '#aaa', fontSize: '0.88rem', marginTop: '0.75rem' }}>{tStudent.practiceSubmitting || 'Preparing questions...'}</p>
    </div>
  );

  // ── DONE / SUMMARY ──
  if (practiceState === 'done' && summary) {
    const pct = summary.score_pct ?? 0;
    const good = pct >= 60;
    return (
      <div style={{ textAlign: 'center', padding: '2rem 1.5rem' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: good ? '#e8f5e9' : '#fff3e0', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem', fontSize: '2rem' }}>
          {good ? '🎉' : '💪'}
        </div>
        <h3 style={{ margin: '0 0 0.25rem', fontSize: '1.1rem' }}>{tStudent.practiceDoneTitle || 'Practice Complete!'}</h3>
        <div style={{ fontSize: '2rem', fontWeight: 800, color: good ? '#2e7d32' : '#e65100', margin: '0.5rem 0' }}>
          {pct}%
        </div>
        <p style={{ color: '#888', fontSize: '0.88rem', marginBottom: '0.25rem' }}>
          {summary.correct ?? 0} / {summary.asked ?? 5} {tStudent.practiceScore || 'correct'}
        </p>
        {summary.new_mastery != null && (
          <p style={{ color: '#667eea', fontSize: '0.85rem', marginBottom: '1.5rem' }}>
            {tStudent.practiceMastery || 'Mastery updated'}: {Math.round(summary.new_mastery * 100)}%
          </p>
        )}
        <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
          <button className="btn-secondary" onClick={handleRetry}>{tStudent.practiceTryAgain || 'Practice Again'}</button>
        </div>
      </div>
    );
  }

  // ── QUESTION / FEEDBACK ──
  const currentQuestion = session?.question;
  const turnNumber = session?.turn_number ?? 1;
  const total = session?.total_questions ?? 5;
  const showFeedback = practiceState === 'feedback' && feedback;

  return (
    <div>
      {/* Progress bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <span style={{ fontSize: '0.82rem', color: '#888', fontWeight: 600 }}>
          {tStudent.practiceQuestion || 'Question'} {turnNumber} {tStudent.practiceOf || '/'} {total}
        </span>
        <div style={{ flex: 1, margin: '0 0.75rem', height: 4, background: '#f0f0f0', borderRadius: 2 }}>
          <div style={{ width: `${((turnNumber - 1) / total) * 100}%`, height: '100%', background: 'linear-gradient(90deg,#667eea,#764ba2)', borderRadius: 2, transition: 'width 0.4s' }} />
        </div>
        <button
          style={{ background: 'none', border: 'none', color: '#aaa', fontSize: '0.8rem', cursor: 'pointer', padding: 0 }}
          onClick={handleStop}
        >
          {tStudent.practiceStop || 'Stop'}
        </button>
      </div>

      {/* Question */}
      <div style={{ background: '#f8f8ff', borderRadius: 10, padding: '1rem 1.25rem', marginBottom: '1rem', borderLeft: '3px solid #667eea' }}>
        <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
          {currentQuestion || ''}
        </ReactMarkdown>
      </div>

      {/* Answer input */}
      <textarea
        ref={answerRef}
        style={{ width: '100%', padding: '0.6rem 0.85rem', borderRadius: 8, border: showFeedback ? `1.5px solid ${feedback?.is_correct ? '#4caf50' : '#e53935'}` : '1.5px solid #e0e0e0', fontSize: '0.9rem', resize: 'vertical', minHeight: 70, boxSizing: 'border-box', fontFamily: 'inherit', background: showFeedback ? (feedback?.is_correct ? '#f1f8e9' : '#fff8f8') : '#fff' }}
        placeholder={tStudent.practiceAnswerPlaceholder || 'Type your answer...'}
        value={answer}
        onChange={e => setAnswer(e.target.value)}
        onKeyDown={handleKeyDown}
        disabled={showFeedback || practiceState === 'evaluating'}
      />

      {/* Feedback block */}
      {showFeedback && (
        <div style={{ margin: '0.75rem 0', padding: '0.75rem 1rem', borderRadius: 8, background: feedback.is_correct ? '#e8f5e9' : '#fff3e0', borderLeft: `3px solid ${feedback.is_correct ? '#4caf50' : '#ff9800'}` }}>
          <div style={{ fontWeight: 700, fontSize: '0.9rem', color: feedback.is_correct ? '#2e7d32' : '#e65100', marginBottom: feedback.feedback ? '0.35rem' : 0 }}>
            {feedback.is_correct ? (tStudent.practiceCorrect || 'Correct! ✓') : (tStudent.practiceWrong || 'Not quite')}
          </div>
          {feedback.feedback && (
            <div style={{ fontSize: '0.87rem', color: '#444', lineHeight: 1.55 }}>
              <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>
                {feedback.feedback}
              </ReactMarkdown>
            </div>
          )}
        </div>
      )}

      {error && <p style={{ color: '#e53935', fontSize: '0.85rem', margin: '0.5rem 0' }}>{error}</p>}

      {/* Action buttons */}
      <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
        {!showFeedback && (
          <button
            className="btn-primary"
            disabled={!answer.trim() || practiceState === 'evaluating'}
            onClick={handleSubmit}
          >
            {practiceState === 'evaluating'
              ? <><span className="spinner spinner-sm" style={{ display: 'inline-block', margin: '0 6px -4px' }} /> {tStudent.practiceSubmitting || 'Checking...'}</>
              : tStudent.practiceSubmit || 'Submit Answer'}
          </button>
        )}
        {showFeedback && feedback?.next_question && (
          <button className="btn-primary" onClick={handleNext}>
            {tStudent.practiceNext || 'Next Question'} <ChevronRight size={15} />
          </button>
        )}
        {showFeedback && !feedback?.next_question && (
          <button className="btn-primary" onClick={handleStop}>
            {tStudent.practiceFinish || 'Finish'} <CheckCircle size={15} />
          </button>
        )}
      </div>
    </div>
  );
}

// ── Flagged Questions Panel ───────────────────────────────────────────────────
function FlaggedQuestionsPanel({ conceptId, tStudent }) {
  const [open, setOpen] = useState(false);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    getMyFlags(conceptId)
      .then(setFlags)
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [conceptId]);

  const handleUnflag = async (questionId) => {
    try {
      await toggleQuestionFlag(questionId);
      setFlags(prev => prev.filter(f => f.question_id !== questionId));
    } catch (e) { console.error(e); }
  };

  if (!loading && flags.length === 0) return null;

  return (
    <div className="studio-card" style={{ marginTop: '1rem' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: 0 }}
      >
        <Bookmark size={16} color="#f59e0b" fill="#f59e0b" />
        <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', textAlign: 'left' }}>
          {tStudent?.flaggedQuestions || 'Flagged for Revision'}
        </span>
        <span style={{ fontSize: '0.72rem', background: '#fef3c7', color: '#92400e', borderRadius: 10, padding: '1px 7px', fontWeight: 700 }}>
          {flags.length}
        </span>
        <ChevronDown size={14} color="#aaa" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={{ marginTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {loading && <div style={{ textAlign: 'center', padding: '0.5rem' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>}
          {flags.map(f => (
            <div key={f.question_id} style={{
              padding: '0.75rem 1rem', borderRadius: 10,
              background: '#fffbeb', border: '1px solid #fde68a',
              display: 'flex', alignItems: 'flex-start', gap: '0.6rem'
            }}>
              <Bookmark size={14} color="#f59e0b" fill="#f59e0b" style={{ flexShrink: 0, marginTop: 3 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ margin: 0, fontSize: '0.87rem', color: '#333', lineHeight: 1.5 }}>{f.text}</p>
                {f.source === 'auto' && (
                  <span style={{ fontSize: '0.7rem', color: '#92400e', fontWeight: 600 }}>
                    {tStudent?.autoFlagged || 'Auto-flagged (wrong answer)'}
                  </span>
                )}
              </div>
              <button
                onClick={() => handleUnflag(f.question_id)}
                title={tStudent?.unflagRevision || 'Remove flag'}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ccc', padding: 2, flexShrink: 0, lineHeight: 1 }}
              >
                <XCircle size={15} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Ask Teacher Panel ─────────────────────────────────────────────────────────
function AskTeacherPanel({ conceptId, tStudent, initialOpen = false, onMessagesRead }) {
  const [open, setOpen] = useState(initialOpen);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(false);
  const bottomRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getConceptMessages(conceptId)
      .then(msgs => {
        setMessages(msgs);
        if (onMessagesRead) onMessagesRead(conceptId);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [open, conceptId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (initialOpen && open) {
      setTimeout(() => panelRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 200);
    }
  }, [initialOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  const handleSend = async () => {
    if (!input.trim() || sending) return;
    const body = input.trim();
    setInput('');
    setSending(true);
    try {
      const msg = await sendConceptMessage(conceptId, body);
      setMessages(prev => [...prev, msg]);
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  return (
    <div ref={panelRef} className="studio-card" style={{ marginTop: '1rem', overflow: 'hidden' }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width: '100%', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', padding: 0 }}
      >
        <MessageCircle size={16} color="#667eea" />
        <span style={{ flex: 1, fontWeight: 700, fontSize: '0.9rem', color: '#1a1a2e', textAlign: 'left' }}>
          {tStudent?.askTeacher || 'Ask your teacher'}
        </span>
        {messages.length > 0 && !open && (
          <span style={{ fontSize: '0.72rem', background: '#667eea', color: '#fff', borderRadius: 10, padding: '1px 7px' }}>{messages.length}</span>
        )}
        <ChevronDown size={14} color="#aaa" style={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
      </button>

      {open && (
        <div style={{ marginTop: '0.75rem' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '1rem' }}><div className="spinner spinner-sm" style={{ margin: 'auto' }} /></div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}>
              {messages.length === 0 && (
                <p style={{ color: '#aaa', fontSize: '0.84rem', textAlign: 'center', margin: '1rem 0' }}>
                  {tStudent?.askTeacherEmpty || 'No messages yet. Ask your teacher anything about this concept.'}
                </p>
              )}
              {messages.map(m => (
                <div key={m.id} style={{
                  alignSelf: m.sender === 'student' ? 'flex-end' : 'flex-start',
                  maxWidth: 'min(80%, 320px)', padding: '0.55rem 0.85rem', borderRadius: 12,
                  background: m.sender === 'student' ? '#667eea' : '#f3f0ff',
                  color: m.sender === 'student' ? '#fff' : '#1a1a2e',
                  fontSize: '0.85rem', lineHeight: 1.5,
                }}>
                  {m.sender === 'teacher' && (
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: '#5B35A0', marginBottom: '0.2rem' }}>
                      {m.sender_name || 'Teacher'}
                    </div>
                  )}
                  {m.body}
                  <div style={{ fontSize: '0.68rem', opacity: 0.6, marginTop: '0.2rem', textAlign: 'right' }}>
                    {new Date(m.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              ))}
              <div ref={bottomRef} />
            </div>
          )}
          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end' }}>
            <textarea
              className="form-textarea"
              rows={2}
              style={{
                flex: 1, fontSize: '0.9rem', resize: 'none',
                padding: '0.6rem 0.85rem', lineHeight: 1.5,
                minHeight: 44, maxHeight: 120,
                borderRadius: 10, boxSizing: 'border-box',
              }}
              placeholder={tStudent?.askTeacherPlaceholder || 'Type your question...'}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || sending}
              style={{
                flexShrink: 0, width: 44, height: 44,
                borderRadius: 10, border: 'none', cursor: 'pointer',
                background: input.trim() ? '#667eea' : '#e8ecf0',
                color: input.trim() ? '#fff' : '#aaa',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                transition: 'background 0.15s',
              }}
            >
              {sending ? <Loader size={16} className="spin" /> : <Send size={16} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function ConceptLearning() {
  const { conceptId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { language } = useLanguage();
  const t = (translations[language]?.studio) || {};
  const tStudent = t.student || {};

  const [concept, setConcept] = useState(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(1);
  const [showTranscriptView, setShowTranscriptView] = useState(false);

  // Chat state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatSessionId, setChatSessionId] = useState(null);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [openerFired, setOpenerFired] = useState(false);
  const [sessionChecked, setSessionChecked] = useState(false);
  const [aiNoticeDismissed, setAiNoticeDismissed] = useState(false);
  // Ref version of openerFired — never stale in closures, prevents double-fire race conditions
  const openerHandledRef = useRef(false);

  const [isPlaying, setIsPlaying] = useState(false);
  const videoRef = useRef(null);
  const lastSentPct = useRef(0);
  const chatEndRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatSectionRef = useRef(null);

  const searchParams = new URLSearchParams(window.location.search);
  const courseId = searchParams.get('course');
  const openChat = searchParams.get('openChat') === '1';
  const studyMode = searchParams.get('study') === 'true';

  useEffect(() => {
    if (courseId) {
      const key = `animlearn_ai_notice_${courseId}`;
      if (localStorage.getItem(key) === 'dismissed') setAiNoticeDismissed(true);
    }
    if (studyMode) setAiNoticeDismissed(true);
  }, [courseId, studyMode]);

  useEffect(() => {
    // Study space mode: concept data comes from router state
    if (studyMode && location.state?.conceptTitle) {
      setConcept({
        id: conceptId,
        title: location.state.conceptTitle,
        summary: location.state.conceptSummary || '',
        subject: location.state.subject || 'general',
        video_url: null,
        mastery_pct: null,
      });
      setLoading(false);
      return;
    }
    if (!courseId) return;
    getMyProgress(courseId)
      .then(data => {
        const found = data.concepts?.find(c => c.id === conceptId);
        setConcept(found || null);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  }, [conceptId, courseId, studyMode, location.state]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!conceptId) return;
    getPixelSessions()
      .then(sessions => {
        const existing = sessions.find(s => s.concept_id === conceptId);
        if (!existing) { setSessionChecked(true); return; }
        return getPixelSession(existing.id).then(data => {
          openerHandledRef.current = true;
          setChatSessionId(existing.id);
          setChatMessages(data.messages.map(m => ({ role: m.role, content: m.content })));
          setOpenerFired(true);
          setSessionChecked(true);
        });
      })
      .catch(() => { setSessionChecked(true); });
  }, [conceptId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  const sendProgress = useCallback((pct) => {
    if (Math.abs(pct - lastSentPct.current) >= 10) {
      lastSentPct.current = pct;
      updateVideoProgress(conceptId, pct).catch(() => {});
    }
  }, [conceptId]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    const handleTime = () => {
      if (video.duration > 0) sendProgress((video.currentTime / video.duration) * 100);
    };
    video.addEventListener('timeupdate', handleTime);
    return () => video.removeEventListener('timeupdate', handleTime);
  }, [concept?.video_url, sendProgress]);

  const fireOpener = useCallback(async () => {
    if (openerHandledRef.current || chatLoading) return;
    openerHandledRef.current = true;
    setOpenerFired(true);
    setChatLoading(true);
    setTimeout(() => chatSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    try {
      const res = await pixelChat({
        subject: concept?.subject || 'general',
        language: language || 'fi',
        conceptId,
        courseId,
        isOpener: true,
      });
      setChatSessionId(res.session_id);
      setChatMessages([{ role: 'assistant', content: res.reply }]);
    } catch {
      // silently ignore
    } finally {
      setChatLoading(false);
    }
  }, [chatLoading, concept, language, conceptId, courseId]); // openerHandledRef is a ref, no need in deps

  // Auto-fire opener in study mode — wait for session check to finish first
  useEffect(() => {
    if (!studyMode || !concept || !sessionChecked || openerHandledRef.current) return;
    fireOpener();
  }, [studyMode, concept, sessionChecked, fireOpener]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleVideoEnded = useCallback(() => {
    updateVideoProgress(conceptId, 100);
    fireOpener();
  }, [conceptId, fireOpener]);

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || chatLoading) return;
    setChatInput('');
    setChatError(null);
    setChatMessages(prev => [...prev, { role: 'user', content: text }]);
    setChatLoading(true);
    try {
      const res = await pixelChat({
        sessionId: chatSessionId,
        message: text,
        subject: concept?.subject || 'general',
        language: language || 'fi',
        conceptId,
        courseId,
      });
      if (!chatSessionId) setChatSessionId(res.session_id);
      setChatMessages(prev => [...prev, { role: 'assistant', content: res.reply }]);
    } catch (err) {
      setChatError(err.message || tStudent.chatError || 'Failed to get response');
      setChatMessages(prev => prev.slice(0, -1));
    } finally {
      setChatLoading(false);
    }
  };

  const handleChatKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); }
  };

  if (loading || !concept) {
    return (
      <div className="studio-page">
        <UnifiedHeader />
        <div className="loading-center"><div className="spinner" /></div>
      </div>
    );
  }

  const mastery = Math.round((concept.mastery_score || 0) * 100);

  const steps = [
    { n: 1, label: tStudent.stepWatch || 'Watch & Chat' },
    { n: 2, label: tStudent.stepPracticeTab || 'Practice' },
  ];

  return (
    <>
    <div className="studio-page">
      <UnifiedHeader subRow={
        <>
          <div className="studio-subheader-left">
            <button className="studio-back-btn" onClick={() => studyMode
              ? navigate('/studio/student', { state: { tab: 'study', studySetId: location.state?.studySetId } })
              : navigate(`/studio/student/courses/${courseId}`)}>
              <ArrowLeft size={15} /> {t.back || 'Back'}
            </button>
            <h1 className="studio-page-title">{concept.title}</h1>
          </div>
          {!studyMode && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <span style={{ fontSize: '0.85rem', color: '#888' }}>{tStudent.mastery || 'Mastery'}:</span>
              <span style={{ fontWeight: 700, color: mastery >= 80 ? '#4caf50' : mastery >= 50 ? '#5B35A0' : '#ff9800' }}>{mastery}%</span>
            </div>
          )}
        </>
      } />

      <div className="studio-main" style={{ maxWidth: 760 }}>

        {/* Step indicator — hidden in study mode */}
        {!studyMode && <div className="step-indicator">
          {steps.map((s, i) => (
            <React.Fragment key={s.n}>
              <div className="step" onClick={() => setStep(s.n)} style={{ cursor: 'pointer' }}>
                <div className={`step-circle ${step > s.n ? 'step-circle-done' : step === s.n ? 'step-circle-active' : 'step-circle-pending'}`}>
                  {step > s.n ? <CheckCircle size={15} /> : s.n}
                </div>
                <span className={`step-label ${step === s.n ? 'active' : ''}`}>{s.label}</span>
              </div>
              {i < steps.length - 1 && <div className={`step-connector ${step > s.n ? 'done' : ''}`} />}
            </React.Fragment>
          ))}
        </div>}

        {/* ── Study mode: chat only (no video step) ── */}
        {studyMode && (
          <div className="studio-card" style={{ padding: 0 }}>
            <div style={{ padding: '1.25rem 1.25rem 0.5rem' }}>
              {concept.summary && (
                <p style={{ margin: 0, color: '#555', fontSize: '0.88rem', lineHeight: 1.6 }}>{concept.summary}</p>
              )}
            </div>
            <div className="concept-chat-section" ref={chatSectionRef}>
              <div className="concept-chat-title">
                <div className="concept-chat-avatar">
                  <div className="pixel-eye-mini pixel-eye-left-mini" />
                  <div className="pixel-eye-mini pixel-eye-right-mini" />
                </div>
                <div style={{ flex: 1 }}>
                  <span>{tStudent.askPixelAbout || 'Ask AnimLearn Tutor about'} <strong>{concept.title}</strong></span>
                </div>
              </div>
              <div className="concept-chat-messages">
                {chatMessages.length === 0 && !chatLoading && (
                  <div className="concept-chat-hint">
                    💡 {tStudent.haveQuestion || 'Have a question about'} <strong>{concept.title}</strong>{tStudent.haveQuestionSuffix || '? Ask me anything — or say "test me" to practice!'}
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`cchat-bubble ${msg.role}`}>
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{msg.content}</ReactMarkdown>
                    ) : msg.content}
                  </div>
                ))}
                {chatLoading && <div className="cchat-bubble assistant cchat-typing"><span /><span /><span /></div>}
                {chatError && <div className="cchat-error">{chatError}</div>}
                <div ref={chatEndRef} />
              </div>
              <div className="concept-chat-input">
                <textarea ref={chatInputRef} className="concept-chat-textarea"
                  placeholder={`${tStudent.askAboutPlaceholder || 'Ask about'} ${concept.title}...`}
                  value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={handleChatKeyDown} rows={1}
                />
                <button className="concept-chat-send" onClick={handleSendChat} disabled={!chatInput.trim() || chatLoading}>
                  {chatLoading ? <Loader size={16} className="spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 1: Watch video + AI Tutor Chat ── */}
        {!studyMode && step === 1 && (
          <div className="studio-card" style={{ padding: 0 }}>
            <div style={{ padding: '1.25rem 1.25rem 1rem' }}>
              <h2 style={{ margin: '0 0 1rem', fontSize: '1.05rem', fontWeight: 700 }}>
                <PlayCircle size={18} style={{ verticalAlign: 'middle', marginRight: 6, color: '#667eea' }} />
                {tStudent.stepWatch || 'Watch Video'}
              </h2>

              {concept.video_url ? (
                <>
                  {!isPlaying ? (
                    <div onClick={() => setIsPlaying(true)} style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: '0.75rem', cursor: 'pointer', background: '#111', aspectRatio: '16/9' }}>
                      {concept.thumbnail_url ? (
                        <img src={concept.thumbnail_url} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                      ) : (
                        <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1a1a2e' }}>
                          <PlayCircle size={48} color="#667eea" />
                        </div>
                      )}
                      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.92)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 4px 20px rgba(0,0,0,0.3)' }}>
                          <PlayCircle size={36} color="#667eea" />
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div style={{ position: 'relative', borderRadius: 10, overflow: 'hidden', marginBottom: '0.75rem', background: '#000', aspectRatio: '16/9' }}>
                      <video ref={videoRef} src={concept.video_url} controls autoPlay
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
                        onEnded={handleVideoEnded}
                      />
                    </div>
                  )}
                  {concept.video_id && <VideoReaction videoId={parseInt(concept.video_id)} showCounts={false} />}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '0.5rem', flexWrap: 'wrap', gap: '0.5rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      {concept.video_watched && (
                        <span className="badge badge-green"><CheckCircle size={12} /> {tStudent.watched || 'Watched'}</span>
                      )}
                      <button className="btn-secondary" style={{ fontSize: '0.82rem', padding: '0.3rem 0.7rem' }} onClick={() => setShowTranscriptView(true)}>
                        <FileText size={13} /> {tStudent.viewTranscript || 'Read Transcript'}
                      </button>
                    </div>
                    <button className="btn-secondary" onClick={() => setStep(2)}>
                      {tStudent.stepPracticeTab || 'Practice'} <ChevronRight size={15} />
                    </button>
                  </div>
                </>
              ) : (
                <div style={{ background: '#f8f9ff', borderRadius: 10, padding: '2rem', textAlign: 'center', color: '#aaa' }}>
                  <BookOpen size={36} style={{ marginBottom: '0.75rem', opacity: 0.4 }} />
                  <p style={{ margin: 0 }}>{tStudent.videoNotReady || 'Video is being prepared by your teacher'}</p>
                </div>
              )}

              {concept.summary && (
                <p style={{ margin: '0.75rem 0 0', color: '#555', fontSize: '0.88rem', lineHeight: 1.6 }}>{concept.summary}</p>
              )}
            </div>

            {/* AI Tutor Chat */}
            <div className="concept-chat-section" ref={chatSectionRef}>
              <div className="concept-chat-title">
                <div className="concept-chat-avatar">
                  <div className="pixel-eye-mini pixel-eye-left-mini" />
                  <div className="pixel-eye-mini pixel-eye-right-mini" />
                </div>
                <div style={{ flex: 1 }}>
                  <span>{tStudent.askPixelAbout || 'Ask AnimLearn Tutor about'} <strong>{concept.title}</strong></span>
                </div>
                {!openerFired && !chatLoading && (
                  <button onClick={fireOpener} style={{ background: 'linear-gradient(135deg,#667eea,#764ba2)', color: '#fff', border: 'none', borderRadius: 20, padding: '0.3rem 0.9rem', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {tStudent.startTutor || 'Start tutor'}
                  </button>
                )}
              </div>

              <div className="concept-chat-messages">
                {courseId && !aiNoticeDismissed && (
                  <div style={{ background: '#f0f4ff', border: '1px solid #dde4ff', borderRadius: 10, padding: '0.65rem 0.85rem', marginBottom: '0.5rem', display: 'flex', alignItems: 'flex-start', gap: '0.6rem' }}>
                    <span style={{ fontSize: '0.8rem', color: '#5B35A0', lineHeight: 1.5, flex: 1 }}>
                      {tStudent.aiNotice || 'Your conversations with the AI tutor may be reviewed by your teacher to ensure quality and appropriate use.'}
                    </span>
                    <button
                      onClick={() => { localStorage.setItem(`animlearn_ai_notice_${courseId}`, 'dismissed'); setAiNoticeDismissed(true); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: '1rem', lineHeight: 1, flexShrink: 0, padding: 0 }}
                      aria-label={tStudent.dismiss || 'Dismiss'}
                    >×</button>
                  </div>
                )}
                {chatMessages.length === 0 && !chatLoading && (
                  <div className="concept-chat-hint">
                    💡 {tStudent.haveQuestion || 'Have a question about'} <strong>{concept.title}</strong>{tStudent.haveQuestionSuffix || '? Ask me anything — or say "test me" to practice!'}
                  </div>
                )}
                {chatMessages.map((msg, i) => (
                  <div key={i} className={`cchat-bubble ${msg.role}`}>
                    {msg.role === 'assistant' ? (
                      <ReactMarkdown remarkPlugins={[remarkMath]} rehypePlugins={[rehypeKatex]}>{msg.content}</ReactMarkdown>
                    ) : msg.content}
                  </div>
                ))}
                {chatLoading && <div className="cchat-bubble assistant cchat-typing"><span /><span /><span /></div>}
                {chatError && <div className="cchat-error">{chatError}</div>}
                <div ref={chatEndRef} />
              </div>

              <div className="concept-chat-input">
                <textarea ref={chatInputRef} className="concept-chat-textarea"
                  placeholder={`${tStudent.askAboutPlaceholder || 'Ask about'} ${concept.title}...`}
                  value={chatInput} onChange={e => setChatInput(e.target.value)} onKeyDown={handleChatKeyDown} rows={1}
                />
                <button className="concept-chat-send" onClick={handleSendChat} disabled={!chatInput.trim() || chatLoading}>
                  {chatLoading ? <Loader size={16} className="spin" /> : <Send size={16} />}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Practice ── */}
        {!studyMode && step === 2 && (
          <div className="studio-card">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
              <h2 style={{ margin: 0, fontSize: '1.05rem', fontWeight: 700 }}>
                <Zap size={18} style={{ verticalAlign: 'middle', marginRight: 6, color: '#667eea' }} />
                {tStudent.stepPracticeTab || 'Practice'}
              </h2>
            </div>
            <PracticeTab conceptId={conceptId} tStudent={tStudent} />
          </div>
        )}

        {/* ── Ask Teacher (hidden in personal study mode) ── */}
        {!studyMode && <AskTeacherPanel conceptId={conceptId} tStudent={tStudent} initialOpen={openChat} />}

        {/* ── Flagged Questions (hidden in personal study mode) ── */}
        {!studyMode && <FlaggedQuestionsPanel conceptId={conceptId} tStudent={tStudent} />}


      </div>
    </div>

    {showTranscriptView && concept && (
      <TranscriptViewModal conceptId={conceptId} conceptTitle={concept.title} onClose={() => setShowTranscriptView(false)} />
    )}
    </>
  );
}
